import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { Box, Button, Flex, Heading, Text, VStack } from 'native-base'
import { useRef, useState } from 'react'
import { Linking } from 'react-native'
import { useDesktopBreakpoint } from '../../../../domain/services/useBreakpoint'
import useLinkHandler from '../../hooks/useLinkHandler'
import { useCabanaApi } from '../../providers/CabanaApiProvider'
import { GuestScreenParams } from '../../screens/GuestScreen'
import { theme } from '../../theme/theme'
import FormAccessoryNavigation from '../_shared/form/FormAccessoryNavigation'
import FormInput from '../_shared/form/FormInput'

interface AccessCodeFormProps {
  userEmail: string
  email?: string
  codeEmailed: boolean
}

const Wave = require('../../assets/images/login/wave-verify.png')

const AccessCodeForm = ({
  userEmail,
  email,
  codeEmailed,
}: AccessCodeFormProps) => {
  const isDesktop = useDesktopBreakpoint()
  const { AuthPasswordlessApi } = useCabanaApi()
  const navigation =
    useNavigation<NativeStackNavigationProp<GuestScreenParams>>()
  const inputRefs = [useRef(), useRef(), useRef()]
  const focusedInput = useRef(0)

  const [error, setError] = useState(<></>)
  const [organizationName, setOrganizationName] = useState('')
  const [createAccountError, setCreateAccountError] = useState(false)
  const [accessCode, setAccessCode] = useState('')
  const [isValid, setIsValid] = useState(false)

  const accessCodeValidation = () =>
    AuthPasswordlessApi.lookupOrganization(accessCode)
      .then((response) => {
        setOrganizationName(response.data.organization)
        setError(<></>)
      })
      .catch((error) => {
        console.debug(error)
        setError(
          <Box mb={5} p={4} bgColor="error.200">
            <Text color="text.900">
              Double check your access code or contact&nbsp;
              <Text
                onPress={() =>
                  Linking.openURL('mailto:support@mycabana.health')
                    .then((data) => console.debug(data))
                    .catch((data) => console.debug(data))
                }
                color={theme.colors.secondary[600]}
                underline>
                support@mycabana.health
              </Text>
            </Text>
          </Box>
        )
        setOrganizationName('')
      })

  const createAccount = () => {
    AuthPasswordlessApi.createAccount(userEmail, accessCode)
      .then(() => {
        navigation.navigate('AccountCreated', { userEmail })
      })
      .catch((error) => {
        console.debug(error)
        setCreateAccountError(true)
      })
  }

  return (
    <>
      {!isDesktop && (
        <Box mt={-4} mx={-6} borderTopWidth={1} borderTopColor="muted.50" />
      )}
      <VStack h="100%" w="100%" space={6}>
        {createAccountError ? (
          <>
            <Heading fontSize="24px" mb="3" mt="3" color="primary.900">
              Uh oh!
            </Heading>
            <Text color="primary.900" fontSize="16px" mb={6}>
              We've encountered an error while creating your account.
            </Text>
            <Button
              onPress={() => {
                Linking.openURL('mailto:support@mycabana.health')
              }}>
              Contact Support
            </Button>
          </>
        ) : (
          <>
            <Heading fontSize={'2xl'} mt="3" color="primary.900">
              Enter your access code
            </Heading>
            <Text color="primary.900" fontSize="18px" fontWeight={600}>
              {userEmail}
            </Text>
            <Text color="primary.900" fontSize="16px">
              {codeEmailed ? (
                <span>
                  We've sent your six-digit access code to{' '}
                  <strong>{email ? email : userEmail}</strong>.
                </span>
              ) : (
                <span>
                  Please enter the six-digit access code provided to you by your
                  organization or sponsor.
                </span>
              )}
            </Text>
            <VStack w="100%" justifyContent="flex-end">
              <FormInput
                bg="white"
                keyboardType="number-pad"
                onChangeText={setAccessCode}
                validation={accessCodeValidation}
                onChangeValid={(valid) => setIsValid(valid)}
                placeholder="Access code"
                defaultValue={accessCode}
                inputAccessoryViewID={'form'}
                inputParamRef={inputRefs[2]}
                onFocusCallback={() => {
                  focusedInput.current = 2
                }}
              />
              {organizationName !== '' && (
                <Flex flexDirection="row" mb={5}>
                  <Text fontWeight={500} color="text.700">
                    Organization:{' '}
                  </Text>
                  <Text fontWeight={500} color="primary.700">
                    {organizationName}
                  </Text>
                </Flex>
              )}
              {error}

              <Button onPress={createAccount} isDisabled={!isValid}>
                Create account
              </Button>
              <Text fontSize="10px" textAlign="center" mt={6} color="text.500">
                {`By signing up, you agree to Cabana by Even Health’s\n`}
                <Text
                  onPress={() =>
                    useLinkHandler('https://mycabana.health/terms-of-service')
                  }
                  underline
                  color="secondary.600">
                  Terms of Service
                </Text>
                {` and `}
                <Text
                  onPress={() =>
                    useLinkHandler('https://www.mycabana.health/privacy-policy')
                  }
                  underline
                  color="secondary.600">
                  Privacy Policy
                </Text>
                .
              </Text>
            </VStack>
          </>
        )}
      </VStack>
      <FormAccessoryNavigation
        inputRefs={inputRefs}
        focusedInput={focusedInput}
      />
    </>
  )
}

export default AccessCodeForm
